import type { Workbook, Worksheet } from 'exceljs';
import moment from 'moment';

import { type DateRange, type Session, Severity, type UUID, type Vehicle, type VehicleGroup } from '@cobliteam/common-types';

import { type Driver, type RoadSpeedLimitAlert, SettingsType, type StopIdleTimeAlert, type UserAlert, type VehicleSpeedLimitAlert } from '@cobliteam/api-lib';

import { getReleaseDate, isEventBasedOnMedianSpeed, periodBeforeReleaseDate, periodContainsReleaseDate } from '@cobliteam/speeding/src/components/SpeedingPage/utils/utils';
import { format } from '@cobliteam/utils/date';

const getSpeedColumnLabels = (session: Session, period: DateRange, doesPeriodContainsReleaseData: boolean) => {
  const isPeriodBeforeReleaseData = periodBeforeReleaseDate(session, period);

  if (isPeriodBeforeReleaseData) {
    return ['Maior velocidade atingida'];
  } else if (doesPeriodContainsReleaseData) {
    return ['Maior velocidade atingida', 'Velocidade média'];
  }
  return ['Velocidade média'];
};

const getSpeedColumnValues = (session: Session, userAlert: RoadSpeedLimitAlert | VehicleSpeedLimitAlert, doesPeriodContainsReleaseData: boolean) => {
  if (doesPeriodContainsReleaseData) {
    if (isEventBasedOnMedianSpeed(session, userAlert.processedAt)) {
      return ['-', userAlert.speedInKmH.toString()];
    } else {
      return [userAlert.speedInKmH.toString(), '-'];
    }
  }

  return [userAlert.speedInKmH.toString()];
};

const getSpeedAlertObservationColumn = (session: Session, userAlert: RoadSpeedLimitAlert | VehicleSpeedLimitAlert, doesPeriodContainsReleaseData: boolean) => {
  const isEventBasedOnPeakSpeed = !isEventBasedOnMedianSpeed(session, userAlert.processedAt);
  if (doesPeriodContainsReleaseData && isEventBasedOnPeakSpeed) {
    const releaseDate = getReleaseDate(session);
    const formattedReleaseDate = format(releaseDate);

    return `Alertas gerados antes de ${formattedReleaseDate} consideram a maior velocidade atingida`;
  }

  return '';
};

export class UserAlertsSpreadsheet {
  private formatDate = (timestamp?: number, format = 'DD/MM/YY'): string => {
    return timestamp ? moment(timestamp).format(format) : '-';
  };

  private formatTime = (timestamp: number): string => moment(timestamp).format('HH:mm');

  private formatDuration = (duration: number): string => moment.utc(moment.duration(duration, 'ms').asMilliseconds()).format('HH:mm');

  private alertTypeLabel = {
    [SettingsType.ROAD_SPEED_LIMIT]: 'Velocidade da via',
    [SettingsType.VEHICLE_SPEED_LIMIT]: 'Velocidade do veículo',
    [SettingsType.STOP_IDLE_TIME]: 'Motor ocioso',
  };

  private fillRow = (sheet: Worksheet, row: number, color: string) => {
    sheet.getRow(row).eachCell({ includeEmpty: true }, (cell) => {
      sheet.getCell(cell.address).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: color },
      };
    });
  };

  private getSpeedSeverity = (speedInKmH: number, speedLimitInKmH: number) => {
    const MEDIUM_INFRACTION_THRESHOLD = 1.2;
    const SEVERE_INFRACTION_THRESHOLD = 1.5;

    const rate = speedInKmH / speedLimitInKmH;

    const isMedium = rate <= MEDIUM_INFRACTION_THRESHOLD;
    const isSevere = rate <= SEVERE_INFRACTION_THRESHOLD;

    if (isMedium) return Severity.MEDIUM;

    if (isSevere) return Severity.SEVERE;

    return Severity.VERY_SEVERE;
  };

  private getStopIdleTimeSeverity = (durationInMillis: number): string => {
    const durationInMinutes = Math.floor(moment.duration(durationInMillis).asMinutes());

    if (durationInMinutes >= 30) return Severity.VERY_SEVERE;

    if (durationInMinutes >= 10) return Severity.SEVERE;

    return Severity.MEDIUM;
  };

  private getBrandModelByVehicle = (vehicle: Vehicle): string => {
    const { brand, name: model } = vehicle.model;

    return !brand && !model ? '-' : `${brand || ''} ${model || ''}`.trim();
  };

  private getDriverName = (driverId: UUID, driversById: Map<UUID, Driver>) => {
    const driver = driversById.get(driverId);
    return driver ? driver.name : 'Sem motorista';
  };

  private toSpeedLimitUserAlertRow =
    (driversById: Map<UUID, Driver>, vehiclesById: Map<UUID, Vehicle>, vehicleGroupsById: Map<UUID, VehicleGroup>, session: Session, doesPeriodContainsReleaseData: boolean) =>
    (userAlert: RoadSpeedLimitAlert | VehicleSpeedLimitAlert): string[] => {
      const vehicle = vehiclesById.get(userAlert.vehicleId);
      const licensePlate = vehicle ? vehicle.licensePlate : '-';
      const brandModel = vehicle ? this.getBrandModelByVehicle(vehicle) : '-';
      const vehicleGroup = userAlert.vehicleGroupId ? vehicleGroupsById.get(userAlert.vehicleGroupId) : undefined;

      return [
        this.formatDate(userAlert.processedAt),
        this.formatTime(userAlert.processedAt),
        this.alertTypeLabel[userAlert.type],
        this.getSpeedSeverity(userAlert.speedInKmH, userAlert.speedLimitInKmH),
        ...getSpeedColumnValues(session, userAlert, doesPeriodContainsReleaseData),
        userAlert.speedLimitInKmH.toString(),
        this.formatDuration(userAlert.durationInMillis),
        licensePlate || '-',
        brandModel,
        userAlert.driverId ? this.getDriverName(userAlert.driverId, driversById) : 'Sem motorista',
        vehicleGroup ? vehicleGroup.name : '-',
        getSpeedAlertObservationColumn(session, userAlert, doesPeriodContainsReleaseData),
      ];
    };

  private toStopIdleTimeAlertRow =
    (driversById: Map<UUID, Driver>, vehiclesById: Map<UUID, Vehicle>, vehicleGroupsById: Map<UUID, VehicleGroup>) => (userAlert: StopIdleTimeAlert) => {
      const vehicle = vehiclesById.get(userAlert.vehicleId);
      const licensePlate = vehicle ? vehicle.licensePlate : '-';
      const brandModel = vehicle ? this.getBrandModelByVehicle(vehicle) : '-';
      const vehicleGroup = userAlert.vehicleGroupId ? vehicleGroupsById.get(userAlert.vehicleGroupId) : undefined;

      return [
        this.formatDate(userAlert.processedAt),
        this.formatTime(userAlert.processedAt),
        this.alertTypeLabel[userAlert.type],
        this.getStopIdleTimeSeverity(userAlert.durationInMillis),
        this.formatDuration(userAlert.durationInMillis),
        licensePlate || '-',
        brandModel,
        userAlert.driverId ? this.getDriverName(userAlert.driverId, driversById) : 'Sem motorista',
        vehicleGroup ? vehicleGroup.name : '-',
      ];
    };

  private addHeader = (sheet: Worksheet, numColumns: number, period: DateRange, sheetTitle: string, isSummary = false) => {
    const emptyArray = [...new Array(numColumns)].map((_) => '');

    const lastColumnLabel = String.fromCharCode('A'.charCodeAt(0) + numColumns - 1);

    sheet.mergeCells(`A1:${lastColumnLabel}1`);
    sheet.getCell('A1').value = `EXPORTADO EM ${moment().format('DD/MM/YYYY')} ÀS ${moment().format('HH:mm')}`;
    sheet.getCell('A1').alignment = { horizontal: 'left' };

    this.fillRow(sheet, 1, '13116e');
    sheet.getRow(1).font = {
      size: 10,
      bold: true,
      color: { argb: 'FFFFFF' },
    };

    sheet.addRow([]);

    sheet.addRow(emptyArray);
    this.fillRow(sheet, 3, 'f4f8f9');

    const periodColumn = isSummary ? 'B' : 'C';
    const sheetTitleColumn = String.fromCharCode(periodColumn.charCodeAt(0) + 1);
    sheet.mergeCells(`${sheetTitleColumn}4:${lastColumnLabel}4`);
    if (!isSummary) {
      sheet.mergeCells('B4:C4');
    }
    sheet.getCell('A4').value = 'Período';
    sheet.getCell(`${periodColumn}4`).value = `De ${this.formatDate(period.start, 'DD/MM/YYYY')} até ${this.formatDate(period.end, 'DD/MM/YYYY')}`;
    sheet.getCell(`${periodColumn}4`).alignment = { horizontal: 'center' };
    sheet.getCell(`${sheetTitleColumn}4`).value = sheetTitle;
    sheet.getCell(`${sheetTitleColumn}4`).alignment = {
      horizontal: 'center',
    };
    this.fillRow(sheet, 4, 'f4f8f9');
    sheet.getRow(4).font = {
      size: 14,
      bold: true,
      color: { argb: '07074e' },
    };

    sheet.addRow(emptyArray);
    this.fillRow(sheet, 5, 'f4f8f9');

    sheet.addRow([]);
  };

  private configSheetColumns = (sheet: Worksheet, numColumns: number, columnWidth = 20) => {
    sheet.columns = [...new Array(numColumns)].map(() => ({
      width: columnWidth,
      style: {
        font: {
          size: 10,
        },
        alignment: {
          vertical: 'middle',
          horizontal: 'center',
        },
      },
    }));
  };

  private buildRoadSpeedEventsSheet = (
    sheet: Worksheet,
    roadSpeedEvents: RoadSpeedLimitAlert[],
    period: DateRange,
    driversById: Map<UUID, Driver>,
    vehiclesById: Map<UUID, Vehicle>,
    vehicleGroupsById: Map<UUID, VehicleGroup>,
    session: Session
  ) => {
    const doesPeriodContainsReleaseData = periodContainsReleaseDate(session, period);
    const speedColumns = getSpeedColumnLabels(session, period, doesPeriodContainsReleaseData);

    const columnNames = [
      'Data',
      'Horário',
      'Tipo de alerta',
      'Gravidade',
      ...speedColumns,
      'Velocidade da via',
      'Duração',
      'Placa',
      'Veículo',
      'Motorista',
      'Grupo',
      'Observações',
    ];

    this.configSheetColumns(sheet, columnNames.length);
    this.addHeader(sheet, columnNames.length, period, 'EXCESSO DE VELOCIDADE DA VIA | COBLI');

    sheet.addRow(columnNames);
    sheet.getRow(7).font = {
      size: 12,
      bold: true,
      color: { argb: '07074e' },
    };
    this.fillRow(sheet, 7, 'f4f7f9');

    sheet.addRows(roadSpeedEvents.map(this.toSpeedLimitUserAlertRow(driversById, vehiclesById, vehicleGroupsById, session, doesPeriodContainsReleaseData)));
  };

  private buildVehicleSpeedEventsSheet = (
    sheet: Worksheet,
    vehicleSpeedEvents: VehicleSpeedLimitAlert[],
    period: DateRange,
    driversById: Map<UUID, Driver>,
    vehiclesById: Map<UUID, Vehicle>,
    vehicleGroupsById: Map<UUID, VehicleGroup>,
    session: Session
  ) => {
    const doesPeriodContainsReleaseData = periodContainsReleaseDate(session, period);
    const speedColumns = getSpeedColumnLabels(session, period, doesPeriodContainsReleaseData);

    const columnNames = [
      'Data',
      'Horário',
      'Tipo de alerta',
      'Gravidade',
      ...speedColumns,
      'Velocidade do veículo',
      'Duração',
      'Placa',
      'Veículo',
      'Motorista',
      'Grupo',
      'Observações',
    ];

    this.configSheetColumns(sheet, columnNames.length);
    this.addHeader(sheet, columnNames.length, period, 'EXCESSO DE VELOCIDADE DO VEÍCULO | COBLI');

    sheet.addRow(columnNames);
    sheet.getRow(7).font = {
      size: 12,
      bold: true,
      color: { argb: '07074e' },
    };
    this.fillRow(sheet, 7, 'f4f7f9');

    sheet.addRows(vehicleSpeedEvents.map(this.toSpeedLimitUserAlertRow(driversById, vehiclesById, vehicleGroupsById, session, doesPeriodContainsReleaseData)));
  };

  private buildStopIdleTimeEventsSheet = (
    sheet: Worksheet,
    stopIdleTimeEvents: StopIdleTimeAlert[],
    period: DateRange,
    driversById: Map<UUID, Driver>,
    vehiclesById: Map<UUID, Vehicle>,
    vehicleGroupsById: Map<UUID, VehicleGroup>
  ) => {
    const columnNames = [
      'Data',
      'Horário',
      'Tipo de alerta',
      'Gravidade',
      'Duração',
      'Placa',
      'Veículo',
      'Motorista',
      'Grupo',
      // 'Endereço de início', TODO: Tech debt?
    ];

    this.configSheetColumns(sheet, columnNames.length);
    this.addHeader(sheet, columnNames.length, period, 'MOTOR OCIOSO | COBLI');

    sheet.addRow(columnNames);
    sheet.getRow(7).font = {
      size: 12,
      bold: true,
      color: { argb: '07074e' },
    };
    this.fillRow(sheet, 7, 'f4f7f9');

    sheet.addRows(stopIdleTimeEvents.map(this.toStopIdleTimeAlertRow(driversById, vehiclesById, vehicleGroupsById)));
  };

  private getDates = (startDate: number, stopDateMillis: number) => {
    const dateArray = [];
    let currentDate = moment(startDate);
    const stopDate = moment(stopDateMillis);
    while (currentDate <= stopDate) {
      dateArray.push(moment(currentDate).format('DD/MM/YYYY'));
      currentDate = moment(currentDate).add(1, 'days');
    }
    return dateArray;
  };

  private buildSummarySheet = (
    sheet: Worksheet,
    period: DateRange,
    roadSpeedEvents: RoadSpeedLimitAlert[],
    vehicleSpeedEvents: VehicleSpeedLimitAlert[],
    stopIdleTimeEvents: StopIdleTimeAlert[]
  ) => {
    const columnNames = ['Resumo do dia', 'Alertas de velocidade da via', 'Alertas de velocidade do veículo', 'Alertas de motor ocioso'];

    this.configSheetColumns(sheet, 4, 35);
    this.addHeader(sheet, 4, period, 'HISTÓRICO DE ALERTAS | COBLI', true);

    sheet.addRow(columnNames);
    sheet.getRow(7).font = {
      size: 12,
      bold: true,
      color: { argb: '07074e' },
    };
    this.fillRow(sheet, 7, 'f4f7f9');

    const dates = this.getDates(period.start, period.end);

    const alertsByDay = [...roadSpeedEvents, ...vehicleSpeedEvents, ...stopIdleTimeEvents].reduce(
      (acc, alert: UserAlert) => {
        const date = this.formatDate(alert.processedAt, 'DD/MM/YYYY');
        if (acc[date]) {
          acc[date].push(alert);
        } else {
          acc[date] = [alert];
        }
        return acc;
      },
      {} as { [key: string]: UserAlert[] }
    );

    dates.forEach((date) => {
      const dailyAlerts = alertsByDay[date] || [];
      const roadSpeedDailyAlerts = dailyAlerts.filter((alert) => alert.type === SettingsType.ROAD_SPEED_LIMIT);
      const vehicleSpeedDailyAlerts = dailyAlerts.filter((alert) => alert.type === SettingsType.VEHICLE_SPEED_LIMIT);
      const stopIdleTimeDailyAlerts = dailyAlerts.filter((alert) => alert.type === SettingsType.STOP_IDLE_TIME);
      sheet.addRow([date, roadSpeedDailyAlerts.length, vehicleSpeedDailyAlerts.length, stopIdleTimeDailyAlerts.length]);
    });

    const totalRowIndex = 8 + dates.length;
    sheet.addRow(['Total', roadSpeedEvents.length, vehicleSpeedEvents.length, stopIdleTimeEvents.length]);
    sheet.getRow(totalRowIndex).font = {
      size: 12,
      bold: true,
      color: { argb: '07074e' },
    };
    this.fillRow(sheet, totalRowIndex, 'f4f7f9');
  };

  private getWorkbook = async (
    userAlerts: UserAlert[],
    period: DateRange,
    driversById: Map<UUID, Driver>,
    vehiclesById: Map<UUID, Vehicle>,
    vehicleGroupsById: Map<UUID, VehicleGroup>,
    session: Session
  ): Promise<Workbook> => {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();

    workbook.creator = 'Cobli';
    workbook.created = moment().toDate();
    workbook.title = 'Relatório de motoristas';

    const roadSpeedEvents = userAlerts.filter((alert) => alert.type === SettingsType.ROAD_SPEED_LIMIT).map((_) => _ as RoadSpeedLimitAlert);

    const vehicleSpeedEvents = userAlerts.filter((alert) => alert.type === SettingsType.VEHICLE_SPEED_LIMIT).map((_) => _ as VehicleSpeedLimitAlert);

    const stopIdleTimeEvents = userAlerts.filter((alert) => alert.type === SettingsType.STOP_IDLE_TIME).map((_) => _ as StopIdleTimeAlert);

    const summarySheet = workbook.addWorksheet(`Resumo ${this.formatDate(period.start, 'DD.MM.YY')} até ${this.formatDate(period.end, 'DD.MM.YY')} `);
    this.buildSummarySheet(summarySheet, period, roadSpeedEvents, vehicleSpeedEvents, stopIdleTimeEvents);

    const roadSpeedEventsSheet = workbook.addWorksheet('Alerta de Velocidade da via');
    this.buildRoadSpeedEventsSheet(roadSpeedEventsSheet, roadSpeedEvents, period, driversById, vehiclesById, vehicleGroupsById, session);

    const vehicleSpeedEventsSheet = workbook.addWorksheet('Alerta de Velocidade do veículo');
    this.buildVehicleSpeedEventsSheet(vehicleSpeedEventsSheet, vehicleSpeedEvents, period, driversById, vehiclesById, vehicleGroupsById, session);

    const stopIdleTimeEventsSheet = workbook.addWorksheet('Alerta de Motor ocioso');
    this.buildStopIdleTimeEventsSheet(stopIdleTimeEventsSheet, stopIdleTimeEvents, period, driversById, vehiclesById, vehicleGroupsById);

    return workbook;
  };

  public buildExcel = async (
    userAlerts: UserAlert[],
    period: DateRange,
    driversById: Map<UUID, Driver>,
    vehiclesById: Map<UUID, Vehicle>,
    vehicleGroupsById: Map<UUID, VehicleGroup>,
    session: Session
  ): Promise<Blob> => {
    const workbook = await this.getWorkbook(userAlerts, period, driversById, vehiclesById, vehicleGroupsById, session);
    const excelBuffer = await workbook.xlsx.writeBuffer();
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

    return new Blob([excelBuffer], { type: fileType });
  };

  public buildCSV = async (
    userAlerts: UserAlert[],
    period: DateRange,
    driversById: Map<UUID, Driver>,
    vehiclesById: Map<UUID, Vehicle>,
    vehicleGroupsById: Map<UUID, VehicleGroup>,
    session: Session
  ): Promise<Blob> => {
    const workbook = await this.getWorkbook(userAlerts, period, driversById, vehiclesById, vehicleGroupsById, session);
    const csvBuffer = await workbook.csv.writeBuffer();

    return new Blob([csvBuffer], { type: 'text/csv' });
  };
}

export default UserAlertsSpreadsheet;
